import React from "react";
import * as Mui from "@mui/material";
import * as env from "shared/env";
import { FullPageModal } from "component/shared/full-page-modal";
import { ListingSearch } from "component/shared/listing-search";
import { User } from "model/user";
import { Lead } from "model/lead";
import { bindActionCreators } from "redux";
import { createSavedSearch, updateSavedSearch } from "redux/slice/saved-searches";
import { connect } from "react-redux";
import { Criterion } from "model/criterion";
import { SavedSearch } from "model/saved-search";

const mapDispatchToProps = (dispatch: any) => bindActionCreators({
	createSavedSearch,
	updateSavedSearch,
}, dispatch)

interface Props extends ReturnType<typeof mapDispatchToProps> {
	user: User;
	lead: Lead;
	open: boolean;
	onClose: Function;
	savedSearch?: SavedSearch;
}

interface State {
	name?: string;
	criteria: Criterion[];
	listingsTotal?: number;
	lowResultsDialogOpen: boolean;
	isTermNonActiveListing: boolean;
	pendingOrSoldDialogOpen: boolean;
}

class Component extends React.Component<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			name: props.savedSearch ? props.savedSearch.name : "",
			criteria: [],
			lowResultsDialogOpen: false,
			isTermNonActiveListing: false,
			pendingOrSoldDialogOpen: false,
		}
	}

	private hasAddressTerm(): boolean {
		const { criteria } = this.state;
		return criteria.some(c => c.name === "term" && !!c.value);
	}

	private handleSaveClick() {
		const { listingsTotal, isTermNonActiveListing } = this.state;
		if (isTermNonActiveListing) {
			this.setState({ pendingOrSoldDialogOpen: true });
			return;
		}
		if (this.hasAddressTerm() && listingsTotal !== undefined && listingsTotal < 3) {
			this.setState({ lowResultsDialogOpen: true });
			return;
		}
		this.persistSearch();
	}

	private persistSearch() {
		const { savedSearch, lead, createSavedSearch, updateSavedSearch, onClose } = this.props;
		const { criteria, name } = this.state;
		if (savedSearch) {
			updateSavedSearch({ savedSearch: {
				...savedSearch,
				name,
				criteria,
			}})
		} else {
			createSavedSearch({ savedSearch: {
				leadId: lead.id,
				name,
				criteria,
				recieveEmails: true,
				inactive: false,
			}});
		}
		onClose();
	}

	public render() {
		const { user, lead, open, onClose, savedSearch } = this.props;
		const { name } = this.state;
		const leadLabel = lead.name ? lead.name : lead.email;

		const actions = (
			<Mui.Button
				variant="contained"
				color="primary"
				onClick={() => this.handleSaveClick()}
			>
				Save & Close
			</Mui.Button>
		);

		return (
			<>
				<Mui.Dialog
					open={this.state.pendingOrSoldDialogOpen}
					onClose={() => this.setState({ pendingOrSoldDialogOpen: false })}
				>
					<Mui.DialogTitle>Pending or sold listings can't be saved</Mui.DialogTitle>
					<Mui.DialogContent>
						<Mui.DialogContentText>
							Saved searches only track active listings. Try broadening your search — adjusting the location, price, or bed count may surface similar active homes nearby.
						</Mui.DialogContentText>
					</Mui.DialogContent>
					<Mui.DialogActions>
						<Mui.Button
							variant="contained"
							color="primary"
							onClick={() => this.setState({ pendingOrSoldDialogOpen: false })}
						>
							Update Criteria
						</Mui.Button>
					</Mui.DialogActions>
				</Mui.Dialog>
				<Mui.Dialog
					open={this.state.lowResultsDialogOpen}
					onClose={() => this.setState({ lowResultsDialogOpen: false })}
				>
					<Mui.DialogTitle>Limited search results</Mui.DialogTitle>
					<Mui.DialogContent>
						<Mui.DialogContentText>
							This search currently has very few matching listings. Expanding your criteria could help you discover more homes and receive better updates.
						</Mui.DialogContentText>
					</Mui.DialogContent>
					<Mui.DialogActions>
						<Mui.Button
							onClick={() => this.setState({ lowResultsDialogOpen: false })}
						>
							Update Criteria
						</Mui.Button>
						<Mui.Button
							variant="contained"
							color="primary"
							onClick={() => {
								this.setState({ lowResultsDialogOpen: false });
								this.persistSearch();
							}}
						>
							Save Search Anyway
						</Mui.Button>
					</Mui.DialogActions>
				</Mui.Dialog>
				<FullPageModal
					open={open}
					onClose={() => onClose()}
					title={`${savedSearch ? "Edit" : "Add a"} Saved Search`}
					leadName={leadLabel}
					actions={actions}
				>
					<Mui.Grid container direction="column" spacing={2}>
						<Mui.Grid item xs={12} sm={6}>
							<Mui.TextField
								value={name}
								variant="outlined"
								label="Saved Search Name (Optional)"
								fullWidth
								onChange={(e) => this.setState({ name: e.currentTarget.value })}
								inputProps={{ maxLength: 256 }}
							/>
						</Mui.Grid>
						<Mui.Grid item>
							<ListingSearch
								user={user}
								onCriteriaChange={(criteria) => this.setState({ criteria })}
								onTermNonActiveListingChange={(value) => this.setState({ isTermNonActiveListing: value })}
								onListingsTotalChange={(listingsTotal) => this.setState({ listingsTotal })}
								criteria={savedSearch && savedSearch.criteria}
								hideSaveListingButton
								hideSaveButton
								disableRegistration
								hideHeartButton
								hideStatusField
								hideModal
								nlsOverride={{
									enabled: user.naturalLanguageSearch,
									onlyMode: !!user.naturalLanguageSearch,
									showSavedSearches: false,
									showMarkets: false,
									showSuggestions: !!user.naturalLanguageSearch,
								}}
								nlsSettingsUrl={user.naturalLanguageSearch === false ? "/settings/search/general" : undefined}
							/>
						</Mui.Grid>
					</Mui.Grid>
				</FullPageModal>
			</>
		);
	}
}

export const SavedSearchDialog = connect(null, mapDispatchToProps)(Component);
